import { Location, NgClass } from '@angular/common'
import { Component, DestroyRef, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { Router } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { all } from 'iso-3166-1'
import { NgArrayPipesModule } from 'ngx-pipes'
import {
  EMPTY,
  catchError,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  take,
} from 'rxjs'

import { ApiService } from '../../api/api.service'
import { SubmitButtonComponent } from '../../components/submit-button/submit-button.component'
import {
  DomainValidator,
  OptionalValidator,
  VatNumberValidator,
} from '../../misc/validators'
import { AuthService } from '../../services/auth/auth.service'
import { OrganizationService } from '../../services/organization/organization.service'

const requiredValidator: ValidatorFn = (control: AbstractControl) =>
  Validators.required(control)

const streetValidator: ValidatorFn = Validators.pattern(/.*\D\d+.*/)

@Component({
  selector: 'dhp-create-organization',
  templateUrl: './create-organization.component.html',
  styleUrl: './create-organization.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    SubmitButtonComponent,
    TranslatePipe,
    NgArrayPipesModule,
  ],
})
export class CreateOrganizationComponent {
  private readonly apiService = inject(ApiService)
  private readonly organizationService = inject(OrganizationService)
  private readonly authService = inject(AuthService)
  private readonly location = inject(Location)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly translate = inject(TranslateService)
  public organizationForm = new FormGroup({
    name: new FormControl<string | null>(null, [requiredValidator]),
    street: new FormControl<string | null>(null, [
      requiredValidator,
      streetValidator,
    ]),
    postal: new FormControl<string | null>(null, [requiredValidator]),
    city: new FormControl<string | null>(null, [requiredValidator]),
    country: new FormControl<string | null>(null, [requiredValidator]),
    vat: new FormControl<string | null>(null, [
      OptionalValidator(VatNumberValidator),
    ]),
    domains: new FormArray<FormControl<string | null>>([]),
  })

  public fields: {
    label: string
    select?: { label: string; value: string }[]
    required?: boolean
  }[] = [
    { label: 'name' },
    { label: 'street' },
    { label: 'postal' },
    { label: 'city' },
    {
      label: 'country',
      select: all()
        .map(c => ({
          label: this.getCountryLabel(c.alpha2, c.country),
          value: c.alpha2,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    { label: 'vat', required: false },
  ]

  readonly isSending = signal(false)
  readonly error = signal<string | null>(null)

  /**
   * Mirrors the domains FormArray. The array itself is not reactive, so a row
   * added outside of a user interaction — the user's own email domain, which is
   * only added once the server confirmed it — would otherwise not be rendered
   * until something else happened to mark the view dirty.
   */
  readonly domainControls = signal<FormControl<string | null>[]>([])

  constructor() {
    this.authService.currentUser$
      .pipe(
        takeUntilDestroyed(),
        filter(Boolean),
        take(1),
        map(user => this.getEmailDomain(user.email)),
        filter((domain): domain is string => domain !== null),
        switchMap(domain =>
          this.apiService.validateOrganizationDomains(new Set([domain])).pipe(
            map(response =>
              response.data?.validateOrganizationDomains.areValid
                ? domain
                : null
            ),
            catchError((err: unknown) => {
              console.error('Error validating user domain:', err)
              return of(null)
            })
          )
        ),
        filter((domain): domain is string => domain !== null)
      )
      .subscribe(domain => {
        if (this.domains.controls.some(control => control.value === domain)) {
          return
        }

        this.addDomain(domain)
      })
  }

  get domains(): FormArray<FormControl<string | null>> {
    return this.organizationForm.controls.domains
  }

  addDomain(domain = ''): void {
    this.domains.push(this.createDomainControl(domain))
    this.syncDomainControls()
  }

  removeDomain(index: number): void {
    this.domains.removeAt(index)
    this.domains.markAsDirty()
    this.domains.markAsTouched()
    this.syncDomainControls()
  }

  private syncDomainControls(): void {
    this.domainControls.set([...this.domains.controls])
  }

  shouldShowDomainErrors(control: FormControl<string | null>): boolean {
    return control.invalid && (control.dirty || control.touched)
  }

  private getCountryLabel(countryCode: string, fallback: string): string {
    const translatedCountry: unknown = this.translate.instant(
      'iso3166.' + countryCode
    )

    return typeof translatedCountry === 'string' ? translatedCountry : fallback
  }

  private createDomainControl(domain = ''): FormControl<string | null> {
    return new FormControl(domain, [requiredValidator, DomainValidator])
  }

  private getEmailDomain(email: string | null): string | null {
    const domain = email?.split('@').pop()?.trim().toLowerCase()
    return domain && domain !== email?.trim().toLowerCase() ? domain : null
  }

  createOrganization(): void {
    if (this.organizationForm.invalid) {
      this.organizationForm.markAllAsTouched()
      return
    }

    const { name, street, postal, city, country, vat } =
      this.organizationForm.value
    const domains = Array.from(
      new Set(
        this.domains.controls
          .map(control => control.value?.trim().toLowerCase())
          .filter((domain): domain is string => Boolean(domain))
      )
    )

    if (!street) {
      this.organizationForm.controls.street.markAsTouched()
      return
    }

    const [, streetName, streetNumber] = street.split(/(.*\D)(\d+.*)/)

    this.error.set(null)
    this.isSending.set(true)
    of(domains)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(validDomains => {
          if (validDomains.length === 0) {
            return of(true)
          }

          return this.apiService
            .validateOrganizationDomains(new Set(validDomains))
            .pipe(
              map(
                response => response.data?.validateOrganizationDomains.areValid
              ),
              catchError((err: unknown) => {
                console.error('Error validating domains:', err)
                this.error.set('pages.organization.create.invalidDomains')
                return EMPTY
              })
            )
        }),
        switchMap(areDomainsValid => {
          if (!areDomainsValid) {
            this.error.set('pages.organization.create.invalidDomains')
            return EMPTY
          }

          return this.organizationService.createOrganization({
            name,
            streetName,
            streetNumber,
            city,
            country,
            postalCode: postal,
            vatNumber: vat,
          })
        }),
        switchMap(organization => {
          if (!organization?.id || domains.length === 0) {
            return of(organization)
          }

          return forkJoin(
            domains.map(domain =>
              this.apiService
                .addDomainForOrganization(organization.id, domain)
                .pipe(
                  catchError((err: unknown) => {
                    console.error(`Error adding domain "${domain}":`, err)
                    return of(null)
                  })
                )
            )
          ).pipe(map(() => organization))
        }),
        switchMap(() => {
          return this.authService.refreshUserDetails()
        }),
        switchMap(() => {
          const { returnTo } = this.location.getState() as { returnTo?: string }
          return this.router.navigate([returnTo ?? '/organization'])
        }),
        catchError((err: unknown) => {
          console.error('Error creating organization:', err)
          this.error.set('pages.organization.create.createError')
          return EMPTY
        }),
        finalize(() => {
          this.isSending.set(false)
        })
      )
      .subscribe()
  }
}
